import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/EmptyState";
import { Radio, ExternalLink, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { showError } from "@/lib/errors";

export default function LivesPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", url: "", platform: "youtube", discipline: "", status: "live" });

  const load = () => api.get("/lives", { params: { limit: 100 } }).then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.url) return toast.error("Titre et URL requis.");
    try { await api.post("/lives", form); toast.success("Live ajouté."); setOpen(false); setForm({ title: "", url: "", platform: "youtube", discipline: "", status: "live" }); load(); }
    catch (e) { showError(e); }
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-end justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">AITO LIVE</h1>
          <p className="text-sm text-muted-foreground mt-1">Les lives sportifs du Fenua — Facebook, YouTube, Twitch.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="lives-add-button" className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"><Plus className="h-4 w-4 mr-1" />Ajouter un live</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Ajouter un live</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="live-title-input" />
              <Input placeholder="URL (YouTube / Facebook)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="live-url-input" />
              <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="youtube">YouTube</SelectItem><SelectItem value="facebook">Facebook</SelectItem><SelectItem value="twitch">Twitch</SelectItem><SelectItem value="other">Autre</SelectItem></SelectContent></Select>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="upcoming">À venir</SelectItem><SelectItem value="live">En cours</SelectItem><SelectItem value="replay">Replay</SelectItem></SelectContent></Select>
              <Button onClick={create} className="w-full bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="live-submit">Ajouter</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {items.length === 0 ? <EmptyState icon={Radio} title="Aucun live programmé." description="Annonce le premier live sportif sur AITO.PF." /> :
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((l) => (
            <Card key={l.id} className="p-4 aito-card-hover" data-testid="live-card">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={l.status === "live" ? "bg-[hsl(var(--aito-energy))] text-black aito-live-dot" : "bg-muted text-foreground"}>{l.status?.toUpperCase()}</Badge>
                <span className="text-xs text-muted-foreground capitalize">{l.platform}</span>
              </div>
              <h3 className="font-display font-semibold leading-tight">{l.title}</h3>
              {l.discipline && <p className="text-xs text-muted-foreground mt-1">{l.discipline}</p>}
              <a href={l.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm text-[hsl(var(--aito-lagoon))] hover:underline" data-testid="live-link"><ExternalLink className="h-3 w-3" />Ouvrir</a>
            </Card>
          ))}
        </div>
      }
    </div>
  );
}
